/**
 * Saved Colours — customer sync orchestration.
 *
 * Bridges the localStorage layer (saved-colours-storage.js) with the
 * custom backend API client (saved-colours-customer-api.js), using the
 * merge utilities (saved-colours-merge.js) to reconcile guest and
 * customer state on login. Every write is debounced so rapid heart-clicks
 * don't spam the API.
 *
 * This module is deliberately tolerant of the customer sync being
 * unavailable (not configured, network failure, backend error): in
 * every failure path it just leaves localStorage as-is and the UI keeps
 * working from local state alone.
 */
(function (global) {
  'use strict';

  var PUSH_DEBOUNCE_MS = 1500;

  var pushTimer = null;
  var status = 'idle'; // idle | pulling | synced | local-only | error
  var statusListeners = [];
  var initialized = false;

  // True only for the duration of this module's own Storage.setState()
  // calls (writing merged data back locally). Storage.setState() notifies
  // *every* subscriber, including the one below that schedules a push —
  // without this guard, persisting a merge result would immediately
  // schedule another push, which persists another (identical) merge
  // result, which schedules another push, forever. Genuine user edits
  // (heart clicks, group changes) never touch this flag, so they still
  // schedule a push normally.
  var isSyncWriting = false;

  function setLocalState(state) {
    isSyncWriting = true;
    global.SavedColoursStorage.setState(state);
    isSyncWriting = false;
  }

  function setStatus(next) {
    status = next;
    statusListeners.forEach(function (fn) {
      try { fn(status); } catch (e) { /* noop */ }
    });
  }

  function onStatusChange(fn) {
    statusListeners.push(fn);
    return function () {
      var i = statusListeners.indexOf(fn);
      if (i > -1) statusListeners.splice(i, 1);
    };
  }

  function isLoggedIn() {
    return !!(global.customerConfig && global.customerConfig.isLoggedIn);
  }

  // Read and write are independently available — a merchant can wire up
  // just the POST endpoint (or just GET) before the other exists.
  function canRead() {
    return isLoggedIn() && global.SavedColoursCustomerAPI && global.SavedColoursCustomerAPI.canRead();
  }

  function canWrite() {
    return isLoggedIn() && global.SavedColoursCustomerAPI && global.SavedColoursCustomerAPI.canWrite();
  }

  function parseRemoteState(rawValue) {
    if (!rawValue) return null;
    try {
      return JSON.parse(rawValue);
    } catch (error) {
      return null;
    }
  }

  /**
   * Pulls the customer's remote state, merges it with whatever is in
   * localStorage, persists the merged result locally, and schedules a
   * write-back so both sides converge. Safe to call multiple times.
   *
   * If the remote fetch can't be reliably read (network error, or a
   * response shape fetchRemoteState() doesn't recognise), this does
   * NOT merge or push at all — it leaves local storage and the server
   * untouched and just tries again on the next sync. Treating an
   * unreadable response as "customer has nothing saved" is exactly what
   * previously caused a real account's saved colours to be merged with
   * nothing and pushed back as empty, wiping them from the server.
   */
  function pullAndMerge() {
    var localState = global.SavedColoursStorage.getState();

    if (!canRead()) {
      // No GET endpoint configured (or not logged in) — nothing to merge
      // in, but still let a configured POST endpoint upload whatever is
      // saved locally so writes work even before reads are wired up.
      setStatus('local-only');
      schedulePush(localState);
      return Promise.resolve(localState);
    }

    setStatus('pulling');

    return global.SavedColoursCustomerAPI.fetchRemoteState().then(function (result) {
      console.log("result::",result);
      // Defensive: fetchRemoteState() is contracted to always resolve an
      // object, but if that contract ever drifts again (as it just did),
      // fail safe here rather than throwing on `result.success` and
      // breaking the promise chain — which previously prevented the
      // push-to-remote subscription below from ever being registered.
      if (!result || !result.success) {
        setStatus('error');
        return localState;
      }

      var remoteState = parseRemoteState(result.value);
      var merged = global.SavedColoursMerge.mergeStates(localState, remoteState);

      setLocalState(merged);
      setStatus('synced');
      schedulePush(merged);
      return merged;
    });
  }

  function schedulePush(state) {
    if (!canWrite()) return;

    clearTimeout(pushTimer);
    pushTimer = setTimeout(function () {
      // Clear before pushNow() so flushPendingPush()'s "is a push still
      // pending?" guard reflects reality once the timer has actually
      // fired — otherwise pushTimer stays a stale truthy id forever after
      // the very first push of the session, and every later tab-hide
      // calls flushPendingPush() unconditionally, firing an unmerged
      // writeRemoteState() and bypassing the merge-before-write step
      // that's supposed to run on every push.
      pushTimer = null;
      pushNow(state || global.SavedColoursStorage.getState());
    }, PUSH_DEBOUNCE_MS);
  }

  /**
   * Writes local state to the backend. Critically, this re-fetches
   * whatever is *currently* on the server and re-merges immediately
   * before writing — not just relying on the merge done once at login.
   *
   * Without this, a second device's session only ever merges against
   * the server once (at its own login), then keeps pushing its own
   * evolving local snapshot for the rest of that session. If a *first*
   * device pushes a change in the meantime, the second device's next
   * push has no idea that happened and silently overwrites it — a
   * classic "merge-on-read-only" race between two active sessions. This
   * closes that window down to the time between this fetch and this
   * write, which is as tight as a client-only (no server-side merge)
   * design can get.
   *
   * If that pre-write fetch can't be reliably read, this ABORTS the
   * write entirely rather than pushing local state on its own — local
   * state alone might be missing colours that only exist on the server,
   * and pushing it would silently erase them.
   */
  function pushNow(state) {
    if (!canWrite()) return Promise.resolve(false);

    var localState = state || global.SavedColoursStorage.getState();
    var ABORT = {};

    var mergeBeforeWrite = canRead()
      ? global.SavedColoursCustomerAPI.fetchRemoteState().then(function (result) {
        console.log(result);
          if (!result || !result.success) return ABORT;
          var remoteState = parseRemoteState(result.value);
          return global.SavedColoursMerge.mergeStates(localState, remoteState);
        })
      : Promise.resolve(localState);

    return mergeBeforeWrite.then(function (merged) {
      if (merged === ABORT) {
        setStatus('error');
        return false;
      }

      // Persist the re-merged result locally too, so this device's own
      // UI reflects whatever the other device had added, not just what
      // it's about to push.
      setLocalState(merged);
      return global.SavedColoursCustomerAPI.writeRemoteState(JSON.stringify(merged)).then(function (ok) {
        // Remote write failed: localStorage already holds the truth, retry on next change.
        setStatus(ok ? 'synced' : 'error');
        return ok;
      });
    });
  }

  /**
   * Wires up automatic sync: pulls + merges remote state once, then keeps
   * pushing local changes (from any source — heart buttons, group edits)
   * back up, debounced.
   */
  function init() {
    if (initialized) return Promise.resolve();
    initialized = true;

    if (!isLoggedIn()) {
      setStatus('local-only');
      return Promise.resolve();
    }

    return pullAndMerge().then(function () {
      global.SavedColoursStorage.subscribe(function (state) {
        if (isSyncWriting) return;
        schedulePush(state);
      });
    });
  }

  /**
   * If a debounced push is still pending when the page is hidden/closed,
   * fire it immediately instead of letting the scheduled setTimeout get
   * killed along with the page — otherwise a save made right before
   * navigating away (a very normal thing to do) would just never reach
   * the server. Uses the current local state directly (skipping the
   * usual re-merge-before-write round trip in pushNow()) since there's
   * no time to spare during unload; the next normal sync will still
   * re-merge properly.
   */
  function flushPendingPush() {
    if (!pushTimer || !canWrite()) return;
    clearTimeout(pushTimer);
    pushTimer = null;
    var state = global.SavedColoursStorage.getState();
    global.SavedColoursCustomerAPI.writeRemoteState(JSON.stringify(state), { keepalive: true });
  }

  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'hidden') flushPendingPush();
  });
  window.addEventListener('pagehide', flushPendingPush);

  global.SavedColoursSync = {
    init: init,
    pullAndMerge: pullAndMerge,
    getStatus: function () { return status; },
    onStatusChange: onStatusChange,
  };
})(window);
